import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalyticEvents } from '@app/enums';
import { JsonApiBody, JsonApiIndexResponse } from '@app/interfaces';
import { KanbanColumn } from '@app/interfaces/kanban-column.interface';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { FunnelTemplateStage } from '@app/models/recruiting/funnel-template-stages.model';
import { FunnelTemplate } from '@app/models/recruiting/funnel-template.model';
import { JobFunnel } from '@app/models/recruiting/job-funnel.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { ApplicantTrackerResources } from '@applicant-tracker/applicant-tracker.resource';
import { FunnelStageAndMessageTemplate } from '@applicant-tracker/interfaces/funnel-stage-and-message-template.interface';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { KanbanApplicant } from '../interfaces/kanban-applicant.interface';

@Injectable()
export class FunnelService {
    jobPosting: JobPosting;

    constructor(
        private httpClient: HttpClient,
        private analyticService: AnalyticService,
        private auth: AuthService,
        private notify: NotifyService
    ) {}

    getKanbanEntities(jobPostingId: number): Promise<KanbanApplicant[]> {
        const url = ApplicantTrackerResources.Kanban.replace(':jobPostingId', jobPostingId.toString());

        return this.httpClient
            .get<JsonApiIndexResponse<JsonApiBody<KanbanApplicant>>>(url)
            .toPromise()
            .then((res) => res.data.map((d) => d.attributes));
    }

    async getFunnelTemplates(): Promise<FunnelTemplate[]> {
        const [funnelTemplates] = await FunnelTemplate.get();
        return funnelTemplates;
    }

    async getFunnelStages(atsFunnelTemplateId: number): Promise<FunnelTemplateStage[]> {
        const [funnelStages] = await FunnelTemplateStage.where('atsFunnelTemplateId', atsFunnelTemplateId)
            .with('funnelMessageTemplate')
            .get();
        return funnelStages;
    }

    convertToJobFunnels(funnelStages: FunnelTemplateStage[]): JobFunnel[] {
        // Save the template stages as job funnels
        const jobFunnelLength = Math.max(
            ...this.jobPosting.jobFunnels.map((jobFunnel: JobFunnel) => {
                return jobFunnel.funnelIndex;
            })
        );
        return funnelStages.map((funnelStage: FunnelTemplateStage) => {
            const jobFunnel = new JobFunnel({
                funnelStep: funnelStage.name,
                // Add to the end of the current funnel
                funnelIndex: jobFunnelLength + funnelStage.stageIndex,
                jobId: this.jobPosting.id,
            });
            return jobFunnel;
        });
    }

    convertToJobFunnelMessageTemplate(
        funnelStages: FunnelTemplateStage[]
    ): Array<JobFunnelMessageTemplate | undefined> {
        return funnelStages.map((funnelStage: FunnelTemplateStage) => {
            return funnelStage.funnelMessageTemplate?.toJobFunnelMessageTemplate();
        });
    }

    convertToFunnelStageAndMessageTemplate(jobFunnels: JobFunnel[]): FunnelStageAndMessageTemplate[] {
        return jobFunnels.map((jobFunnel: JobFunnel) => {
            const funnelTemplateStage = jobFunnel.toFunnelTemplateStage();

            const funnelMessageTemplate = jobFunnel.jobFunnelMessageTemplate?.toFunnelMessageTemplate();

            return { funnelTemplateStage, funnelMessageTemplate };
        });
    }

    convertToTemplateStages(jobFunnels: JobFunnel[]): FunnelTemplateStage[] {
        return jobFunnels.map((jobFunnel: JobFunnel) => {
            return new FunnelTemplateStage({
                name: jobFunnel.funnelStep,
                stageIndex: jobFunnel.funnelIndex,
            });
        });
    }

    saveAllFunnels(jobFunnels: JobFunnel[]): Promise<JobFunnel>[] {
        return jobFunnels.map((jobFunnel: JobFunnel) => {
            return jobFunnel.save();
        });
    }

    async updateApplicantOrder(params: {
        applicantId: number;
        order: number;
        atsJobFunnelId: number;
        showMessages: boolean;
    }): Promise<void> {
        const applicant = new Applicant({
            id: params.applicantId,
            funnelStatus: params.order,
            atsJobFunnelId: params.atsJobFunnelId,
            showMessages: params.showMessages,
        });

        await applicant.param('company', this.auth.company.id).save();

        this.analyticService.trackEvent(AnalyticEvents.RecruitingFunnelStageChange);
    }

    updateColumnOrder(columns: KanbanColumn[], jobPosting: JobPosting): void {
        const promises = columns.map((column) => {
            const jobFunnel: JobFunnel = column.data;
            jobFunnel.funnelIndex = column.index;
            return jobFunnel.param('company', this.auth.company.id).param('jobPosting', jobPosting.id).save();
        });

        Promise.all(promises)
            .then((_) => {
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingFunnelChangeFunnel);
            })
            .catch((err) => {
                this.notify.error(err.message || 'Could not update funnel');
            });
    }

    updateColumnName(update: { id: number; name: string }, jobPosting: JobPosting): void {
        const jobFunnel = jobPosting.jobFunnels.find((jobFunnel) => jobFunnel.id === update.id);
        jobFunnel.funnelStep = update.name;
        jobFunnel
            .param('company', this.auth.company.id)
            .param('jobPosting', jobPosting.id)
            .save()
            .catch((err) => this.notify.error(err.message || 'Could not update funnel'));
    }

    addColumn(jobPosting: JobPosting, temporaryColumnName: string): Promise<JobFunnel> {
        const jobFunnel = new JobFunnel();
        jobFunnel.funnelStep = temporaryColumnName;
        jobFunnel.funnelIndex = jobPosting.jobFunnels.length;
        jobFunnel.jobPostingId = jobPosting.id;
        return jobFunnel.save();
    }

    async deleteColumn(columnId: number, jobPosting: JobPosting): Promise<void> {
        const jobFunnel = jobPosting.jobFunnels.find((jobFunnel: JobFunnel) => jobFunnel.id === columnId);
        await jobFunnel.param('company', this.auth.company.id).param('jobPosting', jobPosting.id).delete();
    }
}
